import { Component, OnInit, signal, inject } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, QueryParamsHandling, Router } from '@angular/router';
import { PageEvent } from '@classi/spen';

import { AuthUserService } from '@services/auth-user.service';
import { MySpaceService } from '../my-space.service';
import { isMobileApp } from '@functions/browser';

import { environment } from '@environments/environment';

import { Album } from '@models/album';
import { isNil, omit, pickBy } from 'lodash';

@Component({
  selector: 'app-albums-list',
  templateUrl: './albums-list.component.html',
  styleUrls: ['./albums-list.component.scss'],
  standalone: false,
})
export class AlbumsListComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private mySpaceService = inject(MySpaceService);
  private authUserService = inject(AuthUserService);

  albumsLoaded = false;
  albums: Album[];
  isShowingFilterModal = false;
  searchKeyword = '';
  private readonly disallowedUrlParamKeys = ['search_key'];
  readonly classiUrl = environment.platformUrl;

  errMsg: string;
  readonly page = signal({
    per_page: 1,
    current_page: 1,
    next_page: 0,
    prev_page: 0,
    total_pages: 1,
    total_count: 0,
  });

  ngOnInit() {
    this.loadList(this.route.snapshot.queryParams);
  }

  private loadList(params: Params): void {
    const pageIndex = +params['page'] || 1;
    this.searchKeyword = params['search_key'] || '';
    this.getAlbumsList({ page: pageIndex });
  }

  private navigate(params: Params, paramsHandling?: QueryParamsHandling): void {
    const apiParams = pickBy(params, (value) => !isNil(value));
    this.loadList(apiParams);

    const urlParams = omit(params, this.disallowedUrlParamKeys);
    const extras: NavigationExtras = { queryParams: urlParams, relativeTo: this.route, replaceUrl: true };
    if (paramsHandling !== undefined) {
      extras.queryParamsHandling = paramsHandling;
    }
    this.router.navigate(['.'], extras);
  }

  getAlbumsList(params: Object) {
    this.albumsLoaded = false;
    const queryParams = Object.assign({}, params, { student_id: this.authUserService.retrieve().id });
    if (this.searchKeyword !== '') {
      Object.assign(queryParams, { search_key: this.searchKeyword });
    }

    this.mySpaceService.getStudentAlbumsList(queryParams).subscribe(
      (response) => {
        this.albums = response.albums;
        this.page.set({ ...this.page(), ...response.pages });
        this.albumsLoaded = true;
      },
      (error) => {
        this.errMsg = <any>error;
        this.albums = [];
      },
    );
  }

  get dataLoaded(): boolean {
    return this.albumsLoaded;
  }

  get albumsCount() {
    return this.page().total_count;
  }

  get showPagination(): boolean {
    return this.page().total_pages > 1;
  }

  pageChanged({ index }: PageEvent) {
    this.navigate({ search_key: this.searchKeyword, page: index + 1 }, 'merge');
    window.scrollTo(0, 0);
  }

  get isMobileApp() {
    return isMobileApp();
  }

  showFilterModal() {
    this.isShowingFilterModal = true;
  }

  onCancelFilter() {
    this.isShowingFilterModal = false;
  }

  removeAlbumFilter() {
    this.navigate({ tab: 'albums' });
  }

  onSubmitFilter(searchKeyword: string) {
    this.isShowingFilterModal = false;
    this.navigate({ tab: 'albums', search_key: searchKeyword });
  }
}
